import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';

const CostDrivers = ({ onShowEvidence }) => {
  const totalSpend = 8750000;
  const monthlyChange = 3.8;

  const costByProgram = [
    { program: "Enterprise Solutions", cost: 2850000, percentage: 32.6 },
    { program: "Cloud Services", cost: 2340000, percentage: 26.7 },
    { program: "Consulting", cost: 1680000, percentage: 19.2 },
    { program: "Support & Maintenance", cost: 1120000, percentage: 12.8 },
    { program: "Training Services", cost: 760000, percentage: 8.7 }
  ];

  const costTrend = [
    { month: "Aug 2025", cost: 7850000, budget: 8000000 },
    { month: "Sep 2025", cost: 8120000, budget: 8200000 },
    { month: "Oct 2025", cost: 8350000, budget: 8400000 },
    { month: "Nov 2025", cost: 8580000, budget: 8600000 },
    { month: "Dec 2025", cost: 8650000, budget: 8700000 },
    { month: "Jan 2026", cost: 8750000, budget: 8800000 }
  ];

  const costPerUnit = [
    { program: "Enterprise Solutions", costPerUnit: 23750, units: 120, efficiency: "Good" },
    { program: "Cloud Services", costPerUnit: 29250, units: 80, efficiency: "Moderate" },
    { program: "Consulting", costPerUnit: 42000, units: 40, efficiency: "Poor" },
    { program: "Support & Maintenance", costPerUnit: 18667, units: 60, efficiency: "Excellent" },
    { program: "Training Services", costPerUnit: 31667, units: 24, efficiency: "Moderate" }
  ];

  const topCostContributors = [
    {
      category: "Personnel Costs",
      amount: 4200000,
      percentage: 48.0,
      trend: "up",
      change: 5.2,
      breakdown: [
        { item: "Salaries & Wages", amount: 3150000 },
        { item: "Benefits & Insurance", amount: 840000 },
        { item: "Training & Development", amount: 210000 }
      ]
    },
    {
      category: "Infrastructure",
      amount: 1750000,
      percentage: 20.0,
      trend: "up",
      change: 8.5,
      breakdown: [
        { item: "Cloud Services", amount: 980000 },
        { item: "Hardware & Equipment", amount: 525000 },
        { item: "Facilities", amount: 245000 }
      ]
    },
    {
      category: "Operations",
      amount: 1400000,
      percentage: 16.0,
      trend: "stable",
      change: 1.2,
      breakdown: [
        { item: "Software Licenses", amount: 630000 },
        { item: "Vendor Services", amount: 490000 },
        { item: "Utilities & Maintenance", amount: 280000 }
      ]
    },
    {
      category: "Marketing & Sales",
      amount: 1050000,
      percentage: 12.0,
      trend: "down",
      change: -2.3,
      breakdown: [
        { item: "Marketing Campaigns", amount: 525000 },
        { item: "Sales Commissions", amount: 350000 },
        { item: "Events & Sponsorships", amount: 175000 }
      ]
    }
  ];

  const costIncreaseDrivers = [
    {
      driver: "Increased Headcount",
      impact: "High",
      amount: 420000,
      percentage: 4.8,
      description: "Addition of 15 new employees across engineering and sales teams",
      action: "Monitor hiring pace and ensure ROI on new positions"
    },
    {
      driver: "Cloud Infrastructure Scaling",
      impact: "High",
      amount: 385000,
      percentage: 4.4,
      description: "Expanded cloud capacity to support growing customer base and new features",
      action: "Optimize resource allocation and implement cost monitoring"
    },
    {
      driver: "Vendor Price Increases",
      impact: "Medium",
      amount: 245000,
      percentage: 2.8,
      description: "Annual price adjustments from key software and service vendors",
      action: "Negotiate better rates and explore alternative vendors"
    },
    {
      driver: "Marketing Campaign Expansion",
      impact: "Medium",
      amount: 180000,
      percentage: 2.1,
      description: "Increased digital advertising spend and new market entry initiatives",
      action: "Track campaign ROI and optimize spend allocation"
    }
  ];

  const COLORS = ['#1E3A5F', '#4A90A4', '#E67E22', '#38A169', '#D69E2E'];

  const getTrendIcon = (trend) => {
    if (trend === 'up') return { name: 'TrendingUp', color: 'var(--color-error)' };
    if (trend === 'down') return { name: 'TrendingDown', color: 'var(--color-success)' };
    return { name: 'Minus', color: 'var(--color-muted-foreground)' };
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency === 'Excellent') return 'var(--color-success)';
    if (efficiency === 'Good') return 'var(--color-secondary)';
    if (efficiency === 'Moderate') return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getImpactColor = (impact) => {
    if (impact === 'High') return 'var(--color-error)';
    if (impact === 'Medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const costByCategory = [
    { category: "Personnel", cost: 12500000, percentage: 68.7, trend: "+5.2%" },
    { category: "Infrastructure", cost: 3200000, percentage: 17.6, trend: "+2.8%" },
    { category: "Operations", cost: 1800000, percentage: 9.9, trend: "-1.5%" },
    { category: "Marketing", cost: 700000, percentage: 3.8, trend: "+8.3%" }
  ];

  const handleExportCSV = () => {
    const costData = costByCategory?.map(item => ({
      Category: item?.category,
      Cost: `$${(item?.cost / 1000000)?.toFixed(2)}M`,
      'Percentage %': item?.percentage,
      Trend: item?.trend
    }));

    exportToCSV({
      title: 'Cost_Drivers_By_Category',
      data: costData
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="Wallet" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Cost Drivers</h2>
            <p className="text-sm text-muted-foreground font-caption">Cost analysis and expense management</p>
          </div>
        </div>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          iconName="Download"
        >
          Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Total Spend</span>
            <Icon name="DollarSign" size={18} color="var(--color-error)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">${(totalSpend / 1000000)?.toFixed(2)}M</p>
          <p className="text-xs text-error font-caption mt-1">+{monthlyChange}% vs last month</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Budget Variance</span>
            <Icon name="Target" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">-0.6%</p>
          <p className="text-xs text-success font-caption mt-1">Under budget</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Avg Cost/Program</span>
            <Icon name="BarChart3" size={18} color="var(--color-secondary)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">${(totalSpend / costByProgram?.length / 1000000)?.toFixed(2)}M</p>
          <p className="text-xs text-muted-foreground font-caption mt-1">Across {costByProgram?.length} programs</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Cost Increase</span>
            <Icon name="TrendingUp" size={18} color="var(--color-warning)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">$1.23M</p>
          <p className="text-xs text-warning font-caption mt-1">From key drivers</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Cost by Program</h3>
          <div className="h-64 md:h-80" aria-label="Cost by Program Pie Chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={costByProgram}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ program, percentage }) => `${program?.split(' ')?.[0]} ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="cost"
                >
                  {costByProgram?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${(value / 1000000)?.toFixed(2)}M`, 'Cost']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {costByProgram?.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS?.[index % COLORS?.length] }} />
                  <span className="text-xs text-muted-foreground font-caption">{item?.program}</span>
                </div>
                <span className="text-xs font-semibold text-foreground font-data">${(item?.cost / 1000000)?.toFixed(2)}M</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Cost by Time Period</h3>
          <div className="h-64 md:h-80" aria-label="Cost Trend Line Chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={costTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${(value / 1000000)?.toFixed(2)}M`, '']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="var(--color-error)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-error)', r: 5 }}
                  name="Actual Cost"
                />
                <Line 
                  type="monotone" 
                  dataKey="budget" 
                  stroke="var(--color-secondary)" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: 'var(--color-secondary)', r: 4 }}
                  name="Budget"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Cost per Program / Unit</h3>
        <div className="space-y-4">
          {costPerUnit?.map((item, index) => (
            <div key={index} className="p-4 rounded-md border border-border bg-muted/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground mb-1">{item?.program}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-caption">
                    <span>{item?.units} units delivered</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <span>Efficiency:</span>
                      <span 
                        className="font-semibold"
                        style={{ color: getEfficiencyColor(item?.efficiency) }}
                      >
                        {item?.efficiency}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-foreground font-data">${(item?.costPerUnit / 1000)?.toFixed(1)}K</p>
                  <p className="text-xs text-muted-foreground font-caption">per unit</p>
                </div>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-smooth"
                  style={{ 
                    width: `${(item?.costPerUnit / 42000) * 100}%`,
                    backgroundColor: getEfficiencyColor(item?.efficiency)
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Top Cost Contributors</h3>
        <div className="space-y-4">
          {topCostContributors?.map((contributor, index) => {
            const trendIcon = getTrendIcon(contributor?.trend);
            return (
              <div key={index} className="p-4 rounded-md border border-border bg-background">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground">{contributor?.category}</h4>
                      <Icon name={trendIcon?.name} size={16} color={trendIcon?.color} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground font-caption">
                      <span>{contributor?.percentage}% of total</span>
                      <span>•</span>
                      <span className={contributor?.trend === 'up' ? 'text-error' : contributor?.trend === 'down' ? 'text-success' : ''}>
                        {contributor?.change > 0 ? '+' : ''}{contributor?.change}% change
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-foreground font-data">${(contributor?.amount / 1000000)?.toFixed(2)}M</p>
                  </div>
                </div>
                <div className="space-y-2 mt-3 pt-3 border-t border-border">
                  {contributor?.breakdown?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground font-caption">{item?.item}</span>
                      <span className="font-semibold text-foreground font-data">${(item?.amount / 1000)?.toFixed(0)}K</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Cost Increase Drivers</h3>
        <div className="space-y-4">
          {costIncreaseDrivers?.map((driver, index) => (
            <div key={index} className="p-4 rounded-md border border-error/30 bg-error/5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground">{driver?.driver}</h4>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-semibold font-caption"
                      style={{ 
                        backgroundColor: `${getImpactColor(driver?.impact)}20`,
                        color: getImpactColor(driver?.impact)
                      }}
                    >
                      {driver?.impact} Impact
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-caption">
                    <span className="font-semibold text-error font-data">+${(driver?.amount / 1000)?.toFixed(0)}K</span>
                    <span>•</span>
                    <span>+{driver?.percentage}% increase</span>
                  </div>
                </div>
                <Icon name="TrendingUp" size={20} color="var(--color-error)" />
              </div>
              <p className="text-xs text-muted-foreground mb-3 font-caption">{driver?.description}</p>
              <div className="flex items-start gap-2 p-3 rounded bg-background">
                <Icon name="Target" size={14} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs text-foreground font-caption">{driver?.action}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CostDrivers;